import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const EditClientDialog = ({ open, onOpenChange, client, onUpdated }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    contact_person: "",
    compuoffice_id: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && client) {
      setForm({
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        address: client.address || "",
        contact_person: client.contact_person || "",
        compuoffice_id: client.compuoffice_id || "",
      });
    }
  }, [open, client]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() ? form.email.trim() : null,
        phone: form.phone.trim() ? form.phone.trim() : null,
        address: form.address.trim() ? form.address.trim() : null,
        contact_person: form.contact_person.trim() || null,
        compuoffice_id: form.compuoffice_id.trim() || null,
      };
      const { data } = await api.patch(`/admin/clients/${client.id}`, payload);
      toast.success(`Client ${data.name} updated`);
      onUpdated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl">
            Edit Client
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Update the client's master details. Client ID cannot be changed.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-client-form">
          <div className="space-y-2">
            <Label className="label-tag">Client ID</Label>
            <Input
              readOnly
              value={client?.client_code || ""}
              className="rounded-md border-slate-200 bg-slate-50 text-slate-600 font-mono tracking-wider"
              data-testid="edit-client-code-readonly"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-tag">Name</Label>
              <Input
                required
                value={form.name}
                onChange={setField("name")}
                data-testid="edit-client-name"
                className="rounded-md border-slate-300"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-tag">Phone</Label>
              <Input
                value={form.phone}
                onChange={setField("phone")}
                data-testid="edit-client-phone"
                className="rounded-md border-slate-300"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="label-tag">
              Contact email <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
            </Label>
            <Input
              type="email"
              value={form.email}
              onChange={setField("email")}
              data-testid="edit-client-email"
              className="rounded-md border-slate-300"
              placeholder="For your records / invoice email delivery"
            />
          </div>

          <div className="space-y-2">
            <Label className="label-tag">Address</Label>
            <Textarea
              rows={2}
              value={form.address}
              onChange={setField("address")}
              data-testid="edit-client-address"
              className="rounded-md border-slate-300 resize-none"
            />
          </div>

          <div className="pt-2 border-t border-slate-100">
            <p className="label-tag mb-3 text-slate-400">Optional details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-tag">Contact person</Label>
                <Input
                  value={form.contact_person}
                  onChange={setField("contact_person")}
                  data-testid="edit-client-contact-person"
                  className="rounded-md border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-tag">CompuOffice ID</Label>
                <Input
                  value={form.compuoffice_id}
                  onChange={setField("compuoffice_id")}
                  data-testid="edit-client-compuoffice-id"
                  className="rounded-md border-slate-300 font-mono"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-300"
              data-testid="cancel-edit-client"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="submit-edit-client"
            >
              {submitting ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
